/* eslint-disable react/no-unused-prop-types */
import type { FC } from 'react';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LinearProgress, {
  linearProgressClasses,
  LinearProgressProps,
} from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import CancelIcon from '@mui/icons-material/Cancel';
import { getExtension } from 'mime';
import { styled } from '@mui/material/styles';
import { BsFillCheckCircleFill } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  flexGrow: 1,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.common.grey,
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: ({ color }: LinearProgressProps) =>
      color === 'success'
        ? theme.palette.success.main
        : theme.palette.primary.main,
  },
}));

const Progress: FC<{ progress?: number | null }> = ({ progress }) => (
  <Typography
    component="span"
    variant="body2"
    display="inline-block"
    fontWeight={600}
    color="primary.main"
    pr={1}
    width="3.5rem"
  >
    &nbsp;-&nbsp;
    {typeof progress === 'number' &&
      progress >= 0 &&
      `${Number(progress).toFixed()}%`}
    {progress === null && (
      <CircularProgress
        sx={{
          height: (theme) => `${theme.typography.body2.fontSize} !important`,
          width: (theme) => `${theme.typography.body2.fontSize} !important`,
        }}
      />
    )}
  </Typography>
);

interface IFileItemProps {
  name: string;
  extension: string;
  readOnly?: boolean;
  progress?: number | null;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (arg: unknown) => void;
  retry?: () => void;
  error?: string;
}

const FileItem: FC<IFileItemProps> = ({
  name,
  extension,
  onDelete,
  retry,
  error,
  progress,
}) => {
  const { t: translate } = useTranslation();
  const fileName = name.slice(0, name.lastIndexOf('.'));
  const docExtension = getExtension(extension);
  const isUploading =
    progress === null || (progress !== undefined && progress >= 0);
  const isUploaded = progress === 100;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      width={1}
      maxWidth={1}
      bgcolor="background.default"
      py={1}
      px={1.75}
      borderRadius="0.5rem"
    >
      <Stack maxWidth="calc(100% - 2.5rem)" flexGrow={1}>
        <Tooltip disableFocusListener placement="top" title={name}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            pb={0.5}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            maxWidth={1}
            color="primary.main"
          >
            {fileName}
          </Typography>
        </Tooltip>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="body2"
            fontWeight="bold"
            color="common.grey"
            minWidth="7rem"
          >
            {docExtension
              ? docExtension.toUpperCase()
              : translate('Common.Unknown')}

            {isUploading && <Progress progress={progress} />}

            {error && (
              <Typography
                component="span"
                variant="body2"
                fontWeight={600}
                color="error.main"
              >
                &nbsp;- {error}
              </Typography>
            )}
          </Typography>

          {isUploading && (
            <BorderLinearProgress
              variant="determinate"
              value={progress ?? 0}
              color={isUploaded ? 'success' : undefined}
            />
          )}
        </Stack>
      </Stack>

      {onDelete && !retry && !isUploaded && (
        <IconButton
          type="button"
          sx={{
            width: '2.5rem',
            color: 'common.grey',
          }}
          onClick={onDelete}
        >
          {isUploading ? <CancelIcon /> : <DeleteOutlineOutlinedIcon />}
        </IconButton>
      )}

      {retry && (
        <IconButton
          type="button"
          sx={{
            width: '2.25rem',
            bgcolor: 'error.main',
            color: 'background.main',
            '&:hover': {
              bgcolor: (theme) => `${theme.palette.error.main}AA`,
            },
          }}
          onClick={retry}
        >
          <ReplayIcon />
        </IconButton>
      )}

      {isUploaded && (
        <Typography
          sx={{
            '& svg': {
              fill: (theme) => theme.palette.success.main,
            },
          }}
          fontSize="h5.fontSize"
          p={1}
        >
          <BsFillCheckCircleFill />
        </Typography>
      )}
    </Stack>
  );
};

export default FileItem;
